import { ColumnContext } from '@/components/Studies/DataTables/Display/DisplayDatatable';
import Button from '@/components/UI/Button';
import DatePickerNative from '@/components/UI/DatePickerNative';
import Select from '@/components/UI/Select';
import type { SelectOption } from '@/components/UI/Select/Select';
import { _isEmpty, _notNil } from '@/littledash';
import type { State } from '@/model/State.model';
import { DateUtils } from '@/utils/Date.utils.ts';
import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import type { FC } from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import type { DataTableColumnType, DataTableObservation } from '../../../DataTable.model';
import type { CustomTableColumnAddProps } from './AddColumn.model';
import { convertMetricsToOptions, convertObservationsToOptions, uniqueNameValidatorFactory } from './AddColumn.util';

type ColumnTypeOption = SelectOption<DataTableColumnType>;

interface CustomTableAddColumnForm {
  type: ColumnTypeOption;
  unit: string | null;
  name?: string;
  measurementMetric?: SelectOption<string>;
  observationId?: SelectOption<string>;
  observationType?: DataTableObservation['display'];
  referenceDate?: string;
}

export const CustomTableColumnAdd: FC<Omit<CustomTableColumnAddProps, 'tableType'>> = ({
  toggleDropdownMenu,
  handleAddColumn,
  columns,
}) => {
  const [refDate, setRefDate] = useState(DateUtils.dateNow());
  const { features } = useSelector(({ team: { features } }: State) => ({
    features,
  }));

  const { metrics, observations } = useContext(ColumnContext);

  const metricOptions = useMemo(() => convertMetricsToOptions(columns, refDate, metrics), [columns, metrics, refDate]);
  const observationsOptions = useMemo(() => convertObservationsToOptions(observations), [observations]);

  let columnTypes: Array<ColumnTypeOption> = [
    {
      label: 'Number',
      value: 'number',
    },
    {
      label: 'Text',
      value: 'text',
    },
    {
      label: 'Formula',
      value: 'formula',
    },
  ];

  if (features?.datatables_observation) {
    columnTypes.push({ label: 'Observation', value: 'observation' });
  }
  columnTypes.push({
    label: 'Timestamp',
    value: 'timestamp',
  });

  if (features?.datatables_measurement) {
    columnTypes = [...columnTypes, { label: 'Measurement', value: 'measurement' }];
  }

  columnTypes.push({
    label: 'Timestamp: PK Dose',
    value: 'timestampBaseline',
  });

  if (columns.some((column) => column.type === 'timestampBaseline')) {
    columnTypes.push({
      label: 'Timestamp: PK Bleed',
      value: 'timestampBaselineRelative',
    });
  }

  const uniqueColumnName = useMemo(
    () => uniqueNameValidatorFactory(new Set(columns.map(({ name }) => name ?? ''))),
    [columns]
  );
  const defaultValues: Partial<CustomTableAddColumnForm> = useMemo(
    () => ({
      type: columnTypes[0],
      name: undefined,
      unit: null,
      measurementMetric: metricOptions?.[0],
      observationId: observationsOptions?.[0],
      observationType: 'score',
      referenceDate: refDate,
    }),
    [refDate]
  );
  const {
    register,
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors, isSubmitting, isValid },
  } = useForm<CustomTableAddColumnForm>({
    defaultValues,
    mode: 'onChange',
  });

  const selectedType = useWatch({ control, name: 'type' });
  const columnType = selectedType?.value as DataTableColumnType | undefined;
  const measurementMetricInvalid = columnType === 'measurement' && _isEmpty(metricOptions);
  const observationOptionsInvalid = columnType === 'observation' && _isEmpty(observationsOptions);
  const emptyMeasurementOption: SelectOption<string> = { value: '', label: 'No options available' };

  useEffect(() => {
    const currentMetric = getValues('measurementMetric');
    if (_isEmpty(metricOptions)) {
      setValue('measurementMetric', emptyMeasurementOption);
      return;
    }
    const hasValidSelection = _notNil(currentMetric) && metricOptions.some((opt) => opt.value === currentMetric.value);
    if (!hasValidSelection) {
      setValue('measurementMetric', metricOptions[0]);
    }
  }, [refDate, metricOptions]);

  const onSubmit = async ({
    type,
    name,
    unit,
    measurementMetric,
    observationId,
    observationType,
    referenceDate,
  }: CustomTableAddColumnForm) => {
    try {
      await handleAddColumn({
        type: type.value,
        name: name?.trim(),
        unit: (unit?.trim() ?? '').length === 0 ? null : unit?.trim(),
        observation:
          _notNil(observationId) && _notNil(observationType) && _notNil(referenceDate)
            ? {
                display: observationType,
                team_glossary_api_id: observationId.value.toString(),
                observed_at: referenceDate,
              }
            : undefined,
        measurement:
          _notNil(measurementMetric) && _notNil(referenceDate)
            ? { metric: measurementMetric.value, measured_at: referenceDate }
            : undefined,
      });
    } catch {
      // `handleAddColumn` is expected to surface its own errors (toast/Sentry); we still close and reset the menu
    } finally {
      toggleDropdownMenu?.(false);
      reset(defaultValues);
    }
  };

  return (
    <div className="pa3" data-test-component="CustomTableColumnAdd">
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <div>
          <Controller
            control={control}
            name="type"
            defaultValue={columnTypes[0]}
            render={({ field: { value, onChange, onBlur } }) => (
              <Select
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                options={columnTypes}
                isMulti={false}
                data-test-element="column-type-select"
              />
            )}
          />
        </div>
        {columnType !== 'measurement' && columnType !== 'observation' && (
          <div className="mt2">
            <input
              type="text"
              autoComplete="off"
              {...register('name', {
                required: 'This field is required',
                maxLength: 255,
                validate: { uniqueColumnName },
                shouldUnregister: true,
              })}
              placeholder="Name"
              aria-label="Column name"
              className={cn({ input__error: _notNil(errors.name) })}
              style={{ marginBottom: 0 }}
              autoFocus
              data-test-element="name-input"
            />
            <ErrorMessage
              errors={errors}
              name="name"
              render={({ message }) => <small className="red db pt2">{message}</small>}
            />
          </div>
        )}
        {(columnType === 'number' || columnType === 'formula') && (
          <div className="mt2">
            <input
              type="text"
              autoComplete="off"
              {...register('unit')}
              placeholder="Unit"
              aria-label="Unit"
              style={{ marginBottom: 0 }}
            />
            <ErrorMessage
              errors={errors}
              name="unit"
              render={({ message }) => <small className="red db pt2">{message}</small>}
            />
          </div>
        )}
        {columnType === 'measurement' && (
          <div className="mt2">
            <Controller
              control={control}
              name="measurementMetric"
              render={({ field: { value, onChange, onBlur } }) => (
                <Select
                  disabled={measurementMetricInvalid}
                  value={value ?? emptyMeasurementOption}
                  onChange={onChange}
                  onBlur={onBlur}
                  options={metricOptions}
                  isMulti={false}
                  data-test-element="measurement-metric-select"
                />
              )}
            />
          </div>
        )}
        {columnType === 'observation' && (
          <>
            <div className="mt2">
              <Controller
                control={control}
                name="observationId"
                render={({ field: { value, onChange, onBlur } }) => (
                  <Select
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    options={observationsOptions}
                    isMulti={false}
                  />
                )}
              />
            </div>
            <div className="mt2">
              <label>Display</label>
              <Controller
                control={control}
                name="observationType"
                render={({ field: { onChange } }) => (
                  <>
                    {[
                      { value: 'score', label: 'Score', checked: true },
                      { value: 'comments', label: 'Comments' },
                    ].map(({ value, label, checked }) => (
                      <div key={value} className="mt2 flex flex-column items-start">
                        <div className="flex flex-row">
                          <input
                            id={`observationType.${value}`}
                            name="observationType"
                            type="radio"
                            value={value}
                            className="pointer ui__form__radio"
                            style={{ marginBottom: 0 }}
                            defaultChecked={checked}
                            onChange={onChange}
                            data-test-element={`observation-type-${value}`}
                            data-testid={`observation-type-${value}`}
                          />
                          <label className="ml2" htmlFor={`observationType.${value}`}>
                            {label}
                          </label>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              />
            </div>
          </>
        )}
        {_notNil(columnType) && ['measurement', 'observation'].includes(columnType) && (
          <div className="mt2">
            <Controller
              control={control}
              name="referenceDate"
              render={({ field: { value, onChange } }) => (
                <DatePickerNative
                  value={value ?? ''}
                  step="any"
                  onChange={(val) => {
                    setRefDate(val);
                    return onChange(val);
                  }}
                />
              )}
            />
          </div>
        )}
        <div className="mt3">
          <Button
            stateless
            className="link db pl0 f6"
            submit
            disabled={isSubmitting || !isValid || measurementMetricInvalid || observationOptionsInvalid}
            data-test-element="add-column-btn"
          >
            Add column
          </Button>
          <Button
            stateless
            className="near-black db pl0 mt2 f6"
            onClick={() => {
              toggleDropdownMenu?.(false);
              reset(defaultValues);
            }}
            data-test-element="cancel-btn"
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};
